import * as THREE from "three";
import { getStarColor } from "../../types";

/** Halve texture sizes on coarse-pointer (touch/mobile) devices */
const IS_MOBILE =
	typeof window !== "undefined" && (window.matchMedia?.("(pointer: coarse)").matches ?? false);

// --- Star glow textures (one per spectral class) ---

const GLOW_TEX_SIZE = IS_MOBILE ? 32 : 64;
const glowTextureCache = new Map<string, THREE.CanvasTexture>();

/** Get (or create) a radial glow texture for the given spectral class */
export function getStarGlowTexture(spectralClass: string): THREE.CanvasTexture {
	const key = spectralClass.charAt(0).toUpperCase();
	let tex = glowTextureCache.get(key);
	if (tex) return tex;

	const canvas = document.createElement("canvas");
	canvas.width = GLOW_TEX_SIZE;
	canvas.height = GLOW_TEX_SIZE;
	const ctx = canvas.getContext("2d");
	if (ctx) {
		const c = GLOW_TEX_SIZE / 2;
		const color = getStarColor(spectralClass);
		const g = ctx.createRadialGradient(c, c, 0, c, c, c);
		g.addColorStop(0, `${color}60`);
		g.addColorStop(0.3, `${color}30`);
		g.addColorStop(0.7, `${color}10`);
		g.addColorStop(1, `${color}00`);
		ctx.beginPath();
		ctx.arc(c, c, c, 0, Math.PI * 2);
		ctx.fillStyle = g;
		ctx.fill();
	}

	tex = new THREE.CanvasTexture(canvas);
	tex.needsUpdate = true;
	glowTextureCache.set(key, tex);
	return tex;
}

// --- System star glow texture ---

const systemStarCache = new Map<string, THREE.CanvasTexture>();

/** Create a rich glow texture for the system-view central star */
export function getSystemStarTexture(spectralClass: string, size: number): THREE.CanvasTexture {
	const key = `${spectralClass.charAt(0).toUpperCase()}:${size}`;
	let tex = systemStarCache.get(key);
	if (tex) return tex;

	const dim = Math.ceil(size * 2) + 2;
	const canvas = document.createElement("canvas");
	canvas.width = dim;
	canvas.height = dim;
	const ctx = canvas.getContext("2d");
	if (ctx) {
		const c = dim / 2;
		const color = getStarColor(spectralClass);

		// Multi-stop radial gradient
		const g = ctx.createRadialGradient(c, c, 0, c, c, size);
		g.addColorStop(0, "#ffffff");
		g.addColorStop(0.12, color);
		g.addColorStop(0.4, `${color}88`);
		g.addColorStop(0.7, `${color}22`);
		g.addColorStop(1.0, `${color}00`);
		ctx.beginPath();
		ctx.arc(c, c, size, 0, Math.PI * 2);
		ctx.fillStyle = g;
		ctx.fill();

		// White-hot core
		ctx.beginPath();
		ctx.arc(c, c, size * 0.18, 0, Math.PI * 2);
		ctx.fillStyle = "#ffffffdd";
		ctx.fill();
	}

	tex = new THREE.CanvasTexture(canvas);
	tex.needsUpdate = true;
	systemStarCache.set(key, tex);
	return tex;
}

// --- Density fog sprite texture ---

let fogTexture: THREE.CanvasTexture | null = null;

/** Soft radial fog gradient for density clusters */
export function getDensityFogTexture(): THREE.CanvasTexture {
	if (fogTexture) return fogTexture;

	const size = IS_MOBILE ? 64 : 128;
	const canvas = document.createElement("canvas");
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext("2d");
	if (ctx) {
		const c = size / 2;
		const g = ctx.createRadialGradient(c, c, 0, c, c, c);
		g.addColorStop(0, "rgba(45, 212, 168, 0.06)");
		g.addColorStop(0.4, "rgba(168, 124, 79, 0.03)");
		g.addColorStop(1, "rgba(0, 0, 0, 0)");
		ctx.beginPath();
		ctx.arc(c, c, c, 0, Math.PI * 2);
		ctx.fillStyle = g;
		ctx.fill();
	}

	fogTexture = new THREE.CanvasTexture(canvas);
	fogTexture.needsUpdate = true;
	return fogTexture;
}

// --- Planet sphere texture ---

const planetTexCache = new Map<string, THREE.CanvasTexture>();

/** Create a 3D-shaded sphere texture for a planet color + type */
export function getPlanetTexture(color: string, isGasGiant: boolean): THREE.CanvasTexture {
	const key = `${color}:${isGasGiant ? "g" : "r"}`;
	let tex = planetTexCache.get(key);
	if (tex) return tex;

	const sz = IS_MOBILE ? 48 : 96;
	const canvas = document.createElement("canvas");
	canvas.width = sz;
	canvas.height = sz;
	const ctx = canvas.getContext("2d");
	if (ctx) {
		const c = sz / 2;
		const r = c - 2; // leave 2px margin for atmospheric rim

		// Base color fill
		ctx.beginPath();
		ctx.arc(c, c, r, 0, Math.PI * 2);
		ctx.fillStyle = color;
		ctx.fill();

		if (isGasGiant) {
			// --- Gas giant atmospheric bands ---
			ctx.save();
			ctx.beginPath();
			ctx.arc(c, c, r, 0, Math.PI * 2);
			ctx.clip();

			// Alternating bands with varying opacity and slight color shifts
			const bandCount = 9;
			const bandHeight = (r * 2) / bandCount;
			for (let i = 0; i < bandCount; i++) {
				const y = c - r + i * bandHeight;
				const isLight = i % 2 === 0;
				ctx.fillStyle = isLight
					? "rgba(255,255,255,0.08)"
					: "rgba(0,0,0,0.12)";
				ctx.fillRect(c - r, y, r * 2, bandHeight);
			}

			// Curved band distortion — elliptical overlay to fake curvature
			const curveGrad = ctx.createRadialGradient(c, c, r * 0.3, c, c, r);
			curveGrad.addColorStop(0, "rgba(0,0,0,0)");
			curveGrad.addColorStop(0.6, "rgba(0,0,0,0)");
			curveGrad.addColorStop(1, "rgba(0,0,0,0.15)");
			ctx.fillStyle = curveGrad;
			ctx.fillRect(c - r, c - r, r * 2, r * 2);

			ctx.restore();
		} else {
			// --- Rocky planet surface texture ---
			ctx.save();
			ctx.beginPath();
			ctx.arc(c, c, r, 0, Math.PI * 2);
			ctx.clip();

			// Pseudo-random surface features (craters / terrain variation)
			// Simple seeded approach using the color string as seed
			let seed = 0;
			for (let i = 0; i < color.length; i++) {
				seed = (seed * 31 + color.charCodeAt(i)) | 0;
			}
			const rng = () => {
				seed = (seed * 1103515245 + 12345) & 0x7fffffff;
				return seed / 0x7fffffff;
			};

			// Scattered darker/lighter patches for terrain
			for (let i = 0; i < 12; i++) {
				const fx = c - r + rng() * r * 2;
				const fy = c - r + rng() * r * 2;
				const fr = r * (0.08 + rng() * 0.18);
				const bright = rng() > 0.5;
				const patchGrad = ctx.createRadialGradient(fx, fy, 0, fx, fy, fr);
				patchGrad.addColorStop(0, bright
					? "rgba(255,255,255,0.12)"
					: "rgba(0,0,0,0.14)");
				patchGrad.addColorStop(1, "rgba(0,0,0,0)");
				ctx.beginPath();
				ctx.arc(fx, fy, fr, 0, Math.PI * 2);
				ctx.fillStyle = patchGrad;
				ctx.fill();
			}

			ctx.restore();
		}

		// --- 3D sphere shading (top-left light source) ---
		const hlGrad = ctx.createRadialGradient(
			c - r * 0.35, c - r * 0.35, r * 0.05,
			c + r * 0.1, c + r * 0.1, r * 1.1,
		);
		hlGrad.addColorStop(0, "rgba(255,255,255,0.45)");
		hlGrad.addColorStop(0.25, "rgba(255,255,255,0.12)");
		hlGrad.addColorStop(0.55, "rgba(0,0,0,0)");
		hlGrad.addColorStop(0.85, "rgba(0,0,0,0.3)");
		hlGrad.addColorStop(1, "rgba(0,0,0,0.45)");
		ctx.beginPath();
		ctx.arc(c, c, r, 0, Math.PI * 2);
		ctx.fillStyle = hlGrad;
		ctx.fill();

		// --- Atmospheric limb brightening (thin bright edge on lit side) ---
		const limbGrad = ctx.createRadialGradient(c, c, r * 0.85, c, c, r);
		limbGrad.addColorStop(0, "rgba(255,255,255,0)");
		limbGrad.addColorStop(0.5, "rgba(200,220,255,0.06)");
		limbGrad.addColorStop(1, "rgba(180,200,255,0.15)");
		ctx.beginPath();
		ctx.arc(c, c, r, 0, Math.PI * 2);
		ctx.fillStyle = limbGrad;
		ctx.fill();
	}

	tex = new THREE.CanvasTexture(canvas);
	tex.needsUpdate = true;
	planetTexCache.set(key, tex);
	return tex;
}

// --- Planet atmospheric glow texture ---

let planetGlowTexture: THREE.CanvasTexture | null = null;

/** Soft radial glow for planet atmosphere halos — tinted via spriteMaterial.color */
export function getPlanetGlowTexture(): THREE.CanvasTexture {
	if (planetGlowTexture) return planetGlowTexture;

	const size = IS_MOBILE ? 32 : 64;
	const canvas = document.createElement("canvas");
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext("2d");
	if (ctx) {
		const c = size / 2;
		const g = ctx.createRadialGradient(c, c, c * 0.35, c, c, c);
		g.addColorStop(0, "rgba(255,255,255,0)");
		g.addColorStop(0.4, "rgba(255,255,255,0.08)");
		g.addColorStop(0.7, "rgba(255,255,255,0.15)");
		g.addColorStop(0.85, "rgba(255,255,255,0.06)");
		g.addColorStop(1, "rgba(255,255,255,0)");
		ctx.beginPath();
		ctx.arc(c, c, c, 0, Math.PI * 2);
		ctx.fillStyle = g;
		ctx.fill();
	}

	planetGlowTexture = new THREE.CanvasTexture(canvas);
	planetGlowTexture.needsUpdate = true;
	return planetGlowTexture;
}

// --- Ambient color patch texture ---

let colorPatchTexture: THREE.CanvasTexture | null = null;

/** Soft white radial gradient — tinted via spriteMaterial.color */
export function getColorPatchTexture(): THREE.CanvasTexture {
	if (colorPatchTexture) return colorPatchTexture;

	const size = IS_MOBILE ? 64 : 128;
	const canvas = document.createElement("canvas");
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext("2d");
	if (ctx) {
		const c = size / 2;
		const g = ctx.createRadialGradient(c, c, 0, c, c, c);
		g.addColorStop(0, "rgba(255,255,255,0.6)");
		g.addColorStop(0.3, "rgba(255,255,255,0.25)");
		g.addColorStop(0.6, "rgba(255,255,255,0.06)");
		g.addColorStop(1, "rgba(255,255,255,0)");
		ctx.beginPath();
		ctx.arc(c, c, c, 0, Math.PI * 2);
		ctx.fillStyle = g;
		ctx.fill();
	}

	colorPatchTexture = new THREE.CanvasTexture(canvas);
	colorPatchTexture.needsUpdate = true;
	return colorPatchTexture;
}

// --- Circle sprite texture (used for Points material map) ---

let circleTexture: THREE.CanvasTexture | null = null;

/** Simple soft circle sprite for PointsMaterial.map */
export function getCircleSpriteTexture(): THREE.CanvasTexture {
	if (circleTexture) return circleTexture;

	const size = IS_MOBILE ? 16 : 32;
	const canvas = document.createElement("canvas");
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext("2d");
	if (ctx) {
		const c = size / 2;
		const g = ctx.createRadialGradient(c, c, 0, c, c, c);
		g.addColorStop(0, "rgba(255,255,255,1)");
		g.addColorStop(0.3, "rgba(255,255,255,0.9)");
		g.addColorStop(0.5, "rgba(255,255,255,0.4)");
		g.addColorStop(0.75, "rgba(255,255,255,0.08)");
		g.addColorStop(1, "rgba(255,255,255,0)");
		ctx.beginPath();
		ctx.arc(c, c, c, 0, Math.PI * 2);
		ctx.fillStyle = g;
		ctx.fill();
	}

	circleTexture = new THREE.CanvasTexture(canvas);
	circleTexture.needsUpdate = true;
	return circleTexture;
}
